import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Etiqueta de estado o de tipo: la pastilla chica de los listados y paneles de las
 * pantallas con diseño ("Propuesta", "Contradicción", "Bloquea", "Validada").
 *
 * No es el `Badge`: aquel es el chip genérico del catálogo (12px/700, con borde).
 * Esta es más chica (10–10,5px/800), sin borde, con el radio de 5–6 px del diseño,
 * y su color lo decide el ESTADO que nombra. Cada tono es un par de tokens
 * fondo/texto —nunca un hex ni un color de Tailwind—, así que acompaña al modo
 * oscuro solo.
 *
 * Los tonos:
 *  - `neutral`        un dato sin carga de estado (tipo "Faltante").
 *  - `tenue`          "pendiente": el neutral con el gris medio, que se lee apagado.
 *  - `success`        validada, respondida, ok.
 *  - `warning`        propuesta, implausible, advierte.
 *  - `info`           editada, criterio.
 *  - `danger`         error, contradicción, bloquea.
 *  - `desactualizada` una redacción validada cuyo hecho citado cambió.
 */
const etiquetaVariants = cva(
  "inline-flex items-center font-extrabold whitespace-nowrap shrink-0 leading-snug",
  {
    variants: {
      tono: {
        neutral: "bg-divisor text-ink-secundario",
        tenue: "bg-divisor text-ink-medio",
        success: "bg-success-soft text-success-soft-foreground",
        warning: "bg-warning-soft text-warning-texto",
        info: "bg-info-soft text-info-soft-foreground",
        danger: "bg-danger-soft text-danger-texto",
        desactualizada: "bg-desactualizada-soft text-desactualizada-foreground",
      },
      size: {
        // Las dos medidas salen del diseño: 10px/radio 5/1×6 en las filas de una
        // lista y 10,5px/radio 6/2×8 en el detalle. En rem, para acompañar la
        // perilla de densidad.
        sm: "rounded-[0.3125rem] px-1.5 py-px text-[0.625rem]",
        md: "rounded-[0.375rem] px-2 py-0.5 text-[0.65625rem]",
      },
    },
    defaultVariants: {
      tono: "neutral",
      size: "md",
    },
  }
)

type TonoDeEtiqueta = NonNullable<VariantProps<typeof etiquetaVariants>["tono"]>

function Etiqueta({
  className,
  tono = "neutral",
  size = "md",
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof etiquetaVariants>) {
  return (
    <span
      data-slot="etiqueta"
      data-tono={tono}
      data-size={size}
      className={cn(etiquetaVariants({ tono, size }), className)}
      {...props}
    />
  )
}

export { Etiqueta, etiquetaVariants }
export type { TonoDeEtiqueta }
